import 'antd/dist/reset.css';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import React, { useMemo, useState, lazy, Suspense } from 'react';
import { AppContext, AuthContext } from './common/constants';
import { AppThemeProvider } from './theme/AppThemeProvider';
import { LocaleProvider } from './common/i18n';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import ProtectedRoute from './components/protectedRoute';
import { UseAuthentification } from './common/hooks';
import AppSplash from './components/appSplash';

// Chargement immédiat : shell de l'app et pages d'entrée
import { Home, Login, MainLayout, MiscError } from './pages';

// Calendriers/dates en français (mois, jours) — global à toute l'app
dayjs.locale('fr');

// Lazy : tout le reste — split par route, charge à la demande
const Clients = lazy(() => import('./pages/clients/clients.page.jsx'));
const DetailClient = lazy(() => import('./pages/clients/detail-client.page.jsx'));
const NouveauClient = lazy(() => import('./pages/clients/nouveau-client.page.jsx'));
const Contrats = lazy(() => import('./pages/contrats/contrats.page.jsx'));
const DetailContrat = lazy(() => import('./pages/contrats/detail-contrat.page.jsx'));
const NouveauContrat = lazy(() => import('./pages/contrats/nouveau-contrat.page.jsx'));
const Reglements = lazy(() => import('./pages/reglements/reglements.page.jsx'));
const NouveauReglement = lazy(() => import('./pages/reglements/nouveau-reglement.page.jsx'));
const EtatReglements = lazy(() => import('./pages/reglements/etat-reglements.page.jsx'));
const DetailReglement = lazy(() => import('./pages/reglements/detail-reglement.page.jsx'));
const Impayes = lazy(() => import('./pages/impayes/impayes.page.jsx'));
const Echeances = lazy(() => import('./pages/echeances/echeances.page.jsx'));
const Sinistres = lazy(() => import('./pages/sinistres/sinistres.page.jsx'));
const NouveauSinistre = lazy(() => import('./pages/sinistres/nouveau-sinistre.page.jsx'));
const DetailSinistre = lazy(() => import('./pages/sinistres/detail-sinistre.page.jsx'));
const Bilans = lazy(() => import('./pages/bilans/bilans.page.jsx'));
const BordereauProduction = lazy(() => import('./pages/bilans/bordereau-production.page.jsx'));
const EtatClients = lazy(() => import('./pages/bilans/etat-clients.page.jsx'));
const EtatClientDetail = lazy(() => import('./pages/bilans/etat-client-detail.page.jsx'));
const SituationCompte = lazy(() => import('./pages/situation-compte/situation.page.jsx'));
const Compagnies = lazy(() => import('./pages/compagnies/compagnies.page.jsx'));
const TypesAssurance = lazy(() => import('./pages/compagnies/types-assurance.page.jsx'));
const Agences = lazy(() => import('./pages/agences/agences.page.jsx'));
const MarquesVehicule = lazy(() => import('./pages/marques-vehicule/marques-vehicule.page.jsx'));
const ReferentielsVehicule = lazy(() => import('./pages/referentiels-vehicule/referentiels-vehicule.page.jsx'));
const Parametrages = lazy(() => import('./pages/parametrages/parametrages.page.jsx'));
const GestionAdmins = lazy(() => import('./pages/admin/gestion-admins.page.jsx'));
const AjouterAdmin = lazy(() => import('./pages/admin/ajouter-admin.page.jsx'));
const Roles = lazy(() => import('./pages/roles/roles.page.jsx'));
const AjouterRole = lazy(() => import('./pages/roles/ajouter-role.page.jsx'));
const AuditLog = lazy(() => import('./pages/audit/audit.page.jsx'));
const Profile = lazy(() => import('./pages/profile/profile.page.jsx'));

// Wrapper : Suspense + ProtectedRoute en un seul endroit
const lazyRoute = (element, perm) => (
  <Suspense fallback={<AppSplash />}>
    {perm ? <ProtectedRoute perm={perm}>{element}</ProtectedRoute> : <ProtectedRoute>{element}</ProtectedRoute>}
  </Suspense>
);

function App() {
  const [appStore, setAppState] = useState({});
  const auth = UseAuthentification();
  const dispatcher = (newState) => setAppState(s => ({ ...s, ...newState }));

  const routers = useMemo(() => createBrowserRouter([
    {
      path: "/dashboard",
      element: <MainLayout />,
      errorElement: <MiscError />,
      children: [
        { path: "", element: <ProtectedRoute perm="dashboard:view"><Home /></ProtectedRoute>, errorElement: <MiscError /> },

        // Clients
        { path: "clients", element: lazyRoute(<Clients />, "clients:read"), errorElement: <MiscError /> },
        { path: "client/:id", element: lazyRoute(<DetailClient />, "clients:read"), errorElement: <MiscError /> },
        { path: "nouveau-client", element: lazyRoute(<NouveauClient />, "clients:create"), errorElement: <MiscError /> },

        // Contrats
        { path: "contrats", element: lazyRoute(<Contrats />, "contrats:read"), errorElement: <MiscError /> },
        { path: "contrat/:id", element: lazyRoute(<DetailContrat />, "contrats:read"), errorElement: <MiscError /> },
        { path: "nouveau-contrat", element: lazyRoute(<NouveauContrat />, "contrats:create"), errorElement: <MiscError /> },

        // Règlements & Impayes
        { path: "reglements", element: lazyRoute(<Reglements />, "reglements:read"), errorElement: <MiscError /> },
        { path: "nouveau-reglement", element: lazyRoute(<NouveauReglement />, "reglements:create"), errorElement: <MiscError /> },
        { path: "etat-reglements", element: lazyRoute(<EtatReglements />, "reglements:read"), errorElement: <MiscError /> },
        { path: "detail-reglement/:id", element: lazyRoute(<DetailReglement />, "reglements:read"), errorElement: <MiscError /> },
        { path: "impayes", element: lazyRoute(<Impayes />, "impayes:read"), errorElement: <MiscError /> },
        { path: "echeances", element: lazyRoute(<Echeances />, "contrats:read"), errorElement: <MiscError /> },

        // Sinistres
        { path: "sinistres", element: lazyRoute(<Sinistres />, "sinistres:read"), errorElement: <MiscError /> },
        { path: "nouveau-sinistre", element: lazyRoute(<NouveauSinistre />, "sinistres:create"), errorElement: <MiscError /> },
        { path: "sinistre/:id", element: lazyRoute(<DetailSinistre />, "sinistres:read"), errorElement: <MiscError /> },

        // Bilans & Situation
        { path: "bilans", element: lazyRoute(<Bilans />, "bilans:read"), errorElement: <MiscError /> },
        { path: "bordereau-production", element: lazyRoute(<BordereauProduction />, "bilans:read"), errorElement: <MiscError /> },
        { path: "etat-clients", element: lazyRoute(<EtatClients />, "bilans:read"), errorElement: <MiscError /> },
        { path: "etat-client/:id", element: lazyRoute(<EtatClientDetail />, "bilans:read"), errorElement: <MiscError /> },
        { path: "situation-compte/:id", element: lazyRoute(<SituationCompte />, "clients:read"), errorElement: <MiscError /> },

        // Configuration
        { path: "compagnies", element: lazyRoute(<Compagnies />, "compagnies:read"), errorElement: <MiscError /> },
        { path: "types-assurance", element: lazyRoute(<TypesAssurance />, "typesAssurance:read"), errorElement: <MiscError /> },
        { path: "agences", element: lazyRoute(<Agences />, "agences:read"), errorElement: <MiscError /> },
        { path: "marques-vehicule", element: lazyRoute(<MarquesVehicule />, "parametrages:read"), errorElement: <MiscError /> },
        { path: "referentiels-vehicule", element: lazyRoute(<ReferentielsVehicule />, "parametrages:read"), errorElement: <MiscError /> },
        { path: "parametrages", element: lazyRoute(<Parametrages />, "parametrages:read"), errorElement: <MiscError /> },

        // Administration
        { path: "gestion-admins", element: lazyRoute(<GestionAdmins />, "users:read"), errorElement: <MiscError /> },
        { path: "ajouter-admin", element: lazyRoute(<AjouterAdmin />, "users:create"), errorElement: <MiscError /> },
        { path: "roles", element: lazyRoute(<Roles />, "roles:read"), errorElement: <MiscError /> },
        { path: "ajouter-role", element: lazyRoute(<AjouterRole />, "roles:create"), errorElement: <MiscError /> },
        { path: "modifier-role/:id", element: lazyRoute(<AjouterRole />, "roles:update"), errorElement: <MiscError /> },
        { path: "audit-log", element: lazyRoute(<AuditLog />, "audit:read"), errorElement: <MiscError /> },

        // Profile (pas de perm requise)
        { path: "profile", element: lazyRoute(<Profile />), errorElement: <MiscError /> },
      ],
    },
    { path: "/", element: <Login />, errorElement: <MiscError /> },
  ]), [auth]);

  if (!auth.initialized) {
    return <AppSplash />;
  }

  return (
    <div className="App">
      <LocaleProvider>
        <AppThemeProvider>
          <AuthContext.Provider value={auth}>
            <AppContext.Provider value={{ store: appStore, dispatcher }}>
              <RouterProvider router={routers} fallbackElement={<AppSplash />} />
            </AppContext.Provider>
          </AuthContext.Provider>
        </AppThemeProvider>
      </LocaleProvider>
    </div>
  );
}

export default App;
